// Single source of truth for site-wide SEO constants and per-route metadata.
//
// Everything crawlable is registered here. `scripts/generate-sitemap.mjs` reads
// this file at build time, so a page added here lands in sitemap.xml without a
// second edit — no hand-maintained URL list to drift (the failure mode in
// form720-ui, where the react-snap route list lives in package.json).
//
// This is deliberately DATA, not components. When the DB-backed CMS lands
// (SEO_CMS_SPEC.md), these rows import straight into `cms_route_meta` and the
// SEO team edits them without a developer. Keep it serialisable.

// The single-vehicle e-file fee every marketing page quotes ("from $5.99").
// Prerendered into titles, descriptions and the Offer markup, so it must match
// the live fee table: scripts/check-price.mjs fails build:prod / build:uat if
// it doesn't (and if VIN corrections stop being free). Change it here only.
export const PRICE_FROM = "5.99";

export const SITE = {
  name: "EasyFile2290",
  // Full word "EasyFile2290" everywhere - easy2290.com is a different company
  // that ranks for the same head term. See SEO_COMPETITOR_ANALYSIS.md.
  url: "https://easyfile2290.com",
  logo: "https://easyfile2290.com/logo.png",
  ogImage: "https://easyfile2290.com/og-image.png",
  twitter: "@easyfile2290",
  // Spellings people type for the brand. Emitted as schema.org alternateName on
  // the WebSite and Organization nodes - Google's documented signal for a
  // site's name - because Google was answering "easyfile2290" with "Showing
  // results for easy 2290" (easy2290.com is a different company).
  alternateNames: ["EasyFile 2290", "Easy File 2290", "EasyFile2290.com"],
  // Profiles that are ours and name the brand exactly "EasyFile2290" (Google
  // Business Profile, LinkedIn, Facebook, X, YouTube ...). Emitted as
  // Organization.sameAs, which is how Google ties those profiles to this site
  // and confirms "EasyFile2290" is a brand, not a typo of "easy file 2290".
  // Add a URL ONLY once the profile is live and owned by us - a sameAs pointing
  // at a page that doesn't exist, or isn't ours, is worse than none.
  // (Nothing listed 2026-10-22: no profile verified yet.)
  sameAs: [],
  description:
    `E-file IRS Form 2290 and get your stamped Schedule 1 in seconds. Heavy Vehicle Use Tax filing from $${PRICE_FROM} — amendments, VIN corrections, and credits included.`,
};

/**
 * Absolute, canonical URL for an app path.
 *
 * ALWAYS trailing-slash, because that is the only form the server answers with
 * 200. react-snap prerenders each route to `build/<path>/index.html`, so nginx
 * sees a directory and 301-redirects `/how-it-works` -> `/how-it-works/`.
 * Emitting the un-slashed form in a canonical or a sitemap therefore points
 * Google at a redirect on every single URL - it still resolves, but it spends
 * crawl budget proving the same thing 21 times and makes the canonical
 * non-self-referencing, which is the one thing a canonical must never be.
 *
 * Every absolute page URL - canonical, og:url, sitemap <loc>, JSON-LD `url` -
 * must come from here so the four can never disagree. The one deliberate
 * exception is JSON-LD `@id` values (`${SITE.url}/#organization`): those are
 * opaque identifiers, not fetchable URLs, and must stay byte-stable across
 * releases or the graph nodes stop joining up.
 *
 * scripts/generate-sitemap.mjs cannot import this file (it is CommonJS-resolved
 * .js parsed as text, by design - see the header there), so it repeats this one
 * rule inline. Change both together.
 */
export const absUrl = (path = "/") => {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${SITE.url}${p.endsWith("/") ? p : `${p}/`}`;
};

const t = (title) => `${title} | ${SITE.name}`;

// Per-route metadata. `priority`/`changefreq` feed sitemap.xml.
//
// Meta descriptions are kept in the 120-158 char band: long enough to earn the
// click, short enough that Google shows the whole thing rather than truncating
// the call to action.
export const ROUTE_META = {
  "/": {
    // Every other route gets the brand appended by t(). The homepage was the
    // one page that named no brand at all, which is backwards: it is the page
    // most likely to be shown for a branded query and the one that has to build
    // the name against easy2290.com, a different company ranking for the same
    // head term. 59 characters, so it survives to the pixel cap intact.
    // Price in the title (2026-10-07): competitors put theirs there ("$9.77/Truck")
    // and ours is the lowest in the market.
    // Brand FIRST (2026-10-22): a search for "easyfile2290" was still answered
    // with "results for easy file 2290" and a page of Easy2290 / EasyForm2290 /
    // other companies. Leading the homepage title with the exact one-word brand
    // is the strongest on-page signal that the name is ours. 46 characters.
    title: `EasyFile2290: File Form 2290 Online from $${PRICE_FROM}`,
    description:
      `EasyFile2290 is an IRS-authorized Form 2290 e-file provider: file from $${PRICE_FROM}, free VIN corrections, stamped Schedule 1 in minutes.`,
    keywords: [
      "easyfile2290",
      "easyfile2290.com",
      "easy file 2290",
      "file form 2290 online",
      "form 2290 e-file",
      "heavy vehicle use tax",
      "hvut filing",
      "irs form 2290",
      "stamped schedule 1",
      // Fleets and tax preparers are the highest-LTV segment and the one
      // competitors under-serve (SEO_COMPETITOR_ANALYSIS.md §5). Bulk upload is
      // already built - it was simply never marketed.
      "bulk 2290 filing",
      "form 2290 bulk upload",
    ],
    priority: 1.0,
    changefreq: "weekly",
  },
  "/how-it-works": {
    title: t("How Form 2290 E-Filing Works"),
    description:
      "See every step of filing Form 2290 — business details, trucks, tax summary, payment, and e-signature — plus the full due-date chart by first-used month.",
    keywords: [
      "how to file form 2290",
      "form 2290 filing steps",
      "form 2290 due date",
      "first used month",
    ],
    priority: 0.9,
    changefreq: "monthly",
  },
  "/rates": {
    title: t("Form 2290 Tax Rates & Weight Categories TY2026"),
    description:
      "Full IRS Form 2290 rate table for tax year 2026-2027: all 21 weight categories A-V, standard and logging rates, plus suspended category W.",
    keywords: [
      "form 2290 tax rates",
      "hvut rates 2026",
      "taxable gross weight categories",
      "form 2290 weight category table",
      "logging vehicle rates",
    ],
    priority: 0.9,
    changefreq: "yearly",
  },
  // Calculators (added 2026-10-07). Transactional-intent tools: each ends in a
  // sign-up / start-filing call to action. Figures: partialPeriodTax.js + RATES
  // (tax), taxPeriods.js (deadlines) - all IRS-transcribed, none computed.
  "/form-2290-tax-calculator": {
    title: t("Form 2290 Tax Calculator 2026-2027 | HVUT Calculator"),
    description:
      "Free Form 2290 tax calculator: enter weight categories and the month first used to get your Heavy Vehicle Use Tax and filing deadline from the IRS tables.",
    keywords: [
      "form 2290 tax calculator",
      "2290 calculator",
      "hvut calculator",
      "heavy vehicle use tax calculator",
      "2290 tax calculator 2026",
      "how much is form 2290",
    ],
    priority: 0.9,
    changefreq: "yearly",
  },
  "/form-2290-due-date-calculator": {
    title: t("Form 2290 Due Date Calculator 2026-2027"),
    description:
      "When is your Form 2290 due? Pick the month your truck was first used to see the IRS filing deadline for the 2026-2027 period, plus the next deadline coming up.",
    keywords: [
      "form 2290 due date calculator",
      "when is form 2290 due",
      "2290 due date",
      "form 2290 deadline",
      "heavy use tax due date",
    ],
    priority: 0.9,
    changefreq: "monthly",
  },
  // IRS reject-code library (added 2026-10-07). Hub only here; the per-code
  // pages come from REJECT_CODE_PAGE_SLUGS in rejectCodes.js (sitemap + link
  // check read that list directly).
  "/form-2290-reject-codes": {
    title: t("Form 2290 Reject Codes: Every IRS Rule and How to Fix It"),
    description:
      "All active IRS Form 2290 reject codes for 2026-2027 in the IRS's own words, what each means, and step-by-step fixes for the ones you can correct yourself.",
    keywords: [
      "form 2290 reject codes",
      "2290 rejection codes",
      "form 2290 rejected",
      "irs 2290 business rules",
      "r0000-922-01",
    ],
    priority: 0.8,
    changefreq: "yearly",
  },
  // Spanish hub (added 2026-10-07). Guides themselves are /es/<slug> from
  // blogPostsEs.js. Title written in Spanish; the brand suffix stays as-is.
  "/es": {
    title: t("Formulario 2290 en Español: Guías para Presentar"),
    description:
      "Guías en español del Formulario 2290: quién debe presentar, fechas de vencimiento, cómo pagar y cómo obtener su Anexo 1 estampado. La aplicación está en inglés.",
    keywords: [
      "formulario 2290 en español",
      "formulario 2290",
      "impuesto 2290 en español",
      "cómo presentar el formulario 2290",
      "anexo 1 formulario 2290",
    ],
    priority: 0.8,
    changefreq: "monthly",
  },
  "/faq": {
    title: t("Form 2290 FAQ — Answers from the IRS Instructions"),
    description:
      "Straight answers on Form 2290: who must file, when it's due, mileage limits, suspended vehicles, EIN requirements, credits, and stamped Schedule 1.",
    keywords: [
      "form 2290 faq",
      "who must file form 2290",
      "form 2290 mileage limit",
      "form 2290 ein requirement",
    ],
    priority: 0.8,
    changefreq: "monthly",
  },
  "/pricing": {
    title: t(`Form 2290 E-File Pricing: From $${PRICE_FROM}, Free VIN Corrections`),
    description:
      "See exactly what EasyFile2290 charges — the per-vehicle e-filing service fee by vehicle count, and discounted return-credit packages, straight from the live rate tables.",
    keywords: [
      "form 2290 pricing",
      "form 2290 e-file cost",
      "hvut filing fee",
      "form 2290 subscription",
    ],
    priority: 0.9,
    changefreq: "weekly",
  },
  "/contact": {
    title: t("Contact Us"),
    description: "Contact EasyFile2290 for Form 2290 filing support, technical assistance, and billing questions.",
    keywords: ["contact easyfile2290", "form 2290 support"],
    priority: 0.4,
    changefreq: "yearly",
  },
  "/about": {
    // Not wrapped in t(): the brand already leads, so the suffix would print
    // "EasyFile2290" twice. This page is the brand's own page - "who is
    // EasyFile2290" - so the name is the subject, not a suffix.
    title: "About EasyFile2290 | IRS-Authorized Form 2290 E-File Provider",
    description:
      `EasyFile2290 (easyfile2290.com) is an IRS-authorized Form 2290 e-file provider from Quantiva LLC. File from $${PRICE_FROM} with free VIN corrections.`,
    keywords: [
      "easyfile2290",
      "easyfile2290.com",
      "easy file 2290",
      "about easyfile2290",
      "is easyfile2290 legit",
      "form 2290 e-file provider",
    ],
    priority: 0.5,
    changefreq: "yearly",
  },
  "/blog": {
    title: t("Form 2290 & HVUT Guides"),
    description:
      "Practical guides to Form 2290 and the Heavy Vehicle Use Tax — deadlines, penalties, amendments, VIN corrections, credits, and stamped Schedule 1.",
    keywords: ["form 2290 guides", "hvut blog", "heavy vehicle use tax guides"],
    priority: 0.8,
    changefreq: "weekly",
  },
  "/terms": {
    title: t("Terms of Service"),
    description: "Terms of service for the EasyFile2290 Form 2290 e-filing application.",
    keywords: [],
    priority: 0.2,
    changefreq: "yearly",
  },
  "/privacy": {
    title: t("Privacy Policy"),
    description: "How EasyFile2290 collects, uses, and protects your Form 2290 filing information.",
    keywords: [],
    priority: 0.2,
    changefreq: "yearly",
  },
};

// Routes that exist but must never be indexed or appear in the sitemap: the
// filing app itself, auth, and anything behind a login.
export const NOINDEX_PREFIXES = [
  "/dashboard",
  "/wizard",
  "/login",
  "/reset-password",
  "/confirm",
];

export const isNoindexPath = (pathname) =>
  NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
